import { State } from '@progress/kendo-data-query';
import { GridDataStateChangeEvent, GridPagerSettings } from '@progress/kendo-react-grid';
import { GridSelectableSettings } from '@progress/kendo-react-grid/dist/npm/interfaces/GridSelectableSettings';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { varianceColumns } from 'src/assets/static/variance/varianceColumns';
import GridWrapper from 'src/components/GridWrapper';
import SelectACompanyPlaceholder from 'src/components/SelectACompanyPlaceholder';
import SelectCompany from 'src/components/SelectCompany';
import useLinkControl from 'src/components/useLinkControl';
import useCompany from 'src/hooks/useCompany';
import useWindowSize from 'src/hooks/useWindowSize';
import useVarianceApi from 'src/services/useVarianceApi';
import { ViewVariance } from 'src/types/ViewVariance';
import exportToExcel from 'src/utils/exportToExcel';
import { modifyRequest } from 'src/utils/requestInterceptor';
import VarianceToolBar from './VarianceToolBar';

const DATA_ITEM_KEY: string = 'varianceId';
const DATE_FIELD = 'poDate';
const dateArr = [DATE_FIELD];

const initialDataState: State = {
  skip: 0,
  take: 20,
  sort: [{ dir: 'desc', field: 'poDate' }],
};
const gridPagerSettings: GridPagerSettings = {
  buttonCount: 5,
  pageSizes: [10, 20, 50, 100],
};
const selectable: GridSelectableSettings = {
  enabled: true,
  drag: false,
  cell: false,
  mode: 'single',
};
const defaultProps = {
  tableTextId: 'variances',
  DataItemKey: DATA_ITEM_KEY,
  sortable: true,
  resizable: true,
  showColumnMenu: true,
  selectable: selectable,
  pageable: gridPagerSettings,
} as const;

const VarianceMain = () => {
  const { company: selectedCompany } = useCompany();
  const [dataState, setDataState] = useState(initialDataState);
  const { getVarianceList, varianceList, isVarianceLoading } = useVarianceApi();
  const [exportLoading, setExportLoading] = useState(false);
  const { LinkControl, openNewTab } = useLinkControl('Variance');

  useEffect(() => {
    if (selectedCompany.companyId) {
      const gridState = { ...modifyRequest(dataState, dateArr, varianceColumns) };
      getVarianceList({ companyId: selectedCompany.companyId, body: gridState });
    }
  }, [dataState, getVarianceList, selectedCompany.companyId]);

  const onDataStateChange = (e: GridDataStateChangeEvent) => {
    setDataState(e.dataState);
  };

  const handleClearFilter = useCallback(() => {
    setDataState((prev) => ({
      ...initialDataState,
      skip: prev.skip,
      take: prev.take,
    }));
  }, []);

  const { height } = useWindowSize();
  const calculateGridHeight = () => `${height - 100}px`;

  const excelExport = useCallback((data: ViewVariance[]) => {
    const columns: string[] = [];
    const columnMapping: Record<string, string> = {};
    [...varianceColumns].forEach((item) => {
      columns.push(item.field as string);
      Object.assign(columnMapping, { [item.field as string]: item.title });
    });
    exportToExcel({ columns, rows: data, fileName: 'Variance', columnMapping });
  }, []);

  const isClearFiltersDisabled = useMemo(
    () => !dataState.filter?.filters.length,
    [dataState.filter?.filters.length]
  );

  const gridToolbarChildren = useMemo(
    () => (
      <VarianceToolBar
        setExportLoading={setExportLoading}
        clearFilter={handleClearFilter}
        excelExport={excelExport}
        rows={varianceList.items}
        dateField={DATE_FIELD}
        varianceDataState={dataState}
        isClearFiltersDisabled={isClearFiltersDisabled}
      />
    ),
    [dataState, excelExport, handleClearFilter, isClearFiltersDisabled, varianceList.items]
  );

  const handleCompanyChange = useCallback(() => {
    setDataState(initialDataState);
  }, []);

  const cellClickFns = useCallback(
    () => ({
      openNewTab,
    }),
    [openNewTab]
  );

  return (
    <>
      <div className="h-14 flex items-center mb-2 justify-between">
        <SelectCompany onChange={handleCompanyChange} className="items-center" />
        <div className="flex-grow" />
        {selectedCompany.companyId ? (
          <div className="mr-2">
            <LinkControl />
          </div>
        ) : (
          ''
        )}
      </div>

      <SelectACompanyPlaceholder
        display={
          <GridWrapper
            columns={varianceColumns}
            dataSource={varianceList?.items}
            height={calculateGridHeight()}
            total={varianceList?.totalRecords}
            onDataStateChange={onDataStateChange}
            dataState={dataState}
            gridToolbarChildren={gridToolbarChildren}
            cellClick={cellClickFns}
            {...defaultProps}
            loading={isVarianceLoading || exportLoading}
          />
        }
      />
    </>
  );
};

export default VarianceMain;
